import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Trash2, Server, Cpu, Clock, FileCode, RefreshCw, Save } from 'lucide-react';
import { useRunner, useUpdateRunner, useDeleteRunner, useRunnerConfig, useUpdateRunnerConfig } from '../hooks/useRunners';
import type { Instance } from '../types/instance';
import InstanceStatusBadge from '../components/InstanceStatusBadge';

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function statusDot(status: string) {
  if (status === 'online') return 'bg-green-500';
  if (status === 'busy') return 'bg-amber-500';
  return 'bg-slate-300';
}

function statusLabel(status: string) {
  if (status === 'online') return 'Online';
  if (status === 'busy') return 'Busy';
  return 'Offline';
}

export default function RunnerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: runner, isLoading } = useRunner(id);
  const updateRunner = useUpdateRunner();
  const deleteRunner = useDeleteRunner();
  const { data: configData, refetch: refetchConfig, isLoading: configLoading } = useRunnerConfig(id);
  const updateConfig = useUpdateRunnerConfig();

  const [name, setName] = useState('');
  const [maxInstances, setMaxInstances] = useState(3);
  const [isDirty, setIsDirty] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'config'>('overview');
  const [configSettings, setConfigSettings] = useState('');
  const [configClaudeMd, setConfigClaudeMd] = useState('');

  useEffect(() => {
    if (runner) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setName(runner.name);
      setMaxInstances(runner.maxInstances);
      setIsDirty(false);
    }
  }, [runner]);

  useEffect(() => {
    if (configData?.config) {
      const config = configData.config as Record<string, unknown>;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setConfigSettings(JSON.stringify(config.settings || {}, null, 2));
      setConfigClaudeMd((config.claudeMd as string) || '');
    }
  }, [configData]);

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="text-sm text-slate-400">Loading runner...</div>
      </div>
    );
  }

  if (!runner) {
    return (
      <div className="p-8">
        <div className="text-sm text-slate-500">Runner not found.</div>
      </div>
    );
  }

  const instances = (runner as unknown as { instances?: Instance[] }).instances;

  const handleSave = async () => {
    if (!id) return;
    await updateRunner.mutateAsync({ id, name, maxInstances });
    setIsDirty(false);
  };

  const handleSaveConfig = async () => {
    if (!id) return;
    let settings: Record<string, unknown> = {};
    try {
      settings = JSON.parse(configSettings);
    } catch {
      alert('Invalid JSON in settings');
      return;
    }
    await updateConfig.mutateAsync({
      id,
      config: { settings, claudeMd: configClaudeMd },
    });
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!confirm('Delete this runner? This cannot be undone.')) return;
    await deleteRunner.mutateAsync(id);
    navigate('/runners');
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/runners"
          className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Runners
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100">
              <Server className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{runner.name}</h1>
              <div className="mt-0.5 flex items-center gap-2 text-sm text-slate-500">
                <span className={`h-2 w-2 rounded-full ${statusDot(runner.status)}`} />
                <span>{statusLabel(runner.status)}</span>
                <span className="text-slate-300">|</span>
                <span>{runner.hostname}</span>
              </div>
            </div>
          </div>
          <button
            onClick={handleDelete}
            className="flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-1 rounded-lg bg-slate-100 p-1">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'overview'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Server className="h-4 w-4" />
          Overview
        </button>
        <button
          onClick={() => setActiveTab('config')}
          className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'config'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <FileCode className="h-4 w-4" />
          Claude Config
        </button>
      </div>

      {activeTab === 'overview' && (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Settings */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="mb-4 text-sm font-semibold text-slate-900">Configuration</h2>
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setIsDirty(true); }}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Max Instances: {maxInstances}
                </label>
                <input
                  type="range"
                  min={1}
                  max={20}
                  value={maxInstances}
                  onChange={(e) => { setMaxInstances(Number(e.target.value)); setIsDirty(true); }}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-slate-400">
                  <span>1</span>
                  <span>20</span>
                </div>
              </div>
              {isDirty && (
                <button
                  onClick={handleSave}
                  disabled={updateRunner.isPending}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {updateRunner.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              )}
            </div>
          </div>

          {/* Instances on this runner */}
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="mb-4 text-sm font-semibold text-slate-900">Recent Instances</h2>
            {instances && instances.length > 0 ? (
              <div className="space-y-2">
                {instances.map((inst) => (
                  <Link
                    key={inst.id}
                    to={`/instances/${inst.id}`}
                    className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3 hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-3">
                      <InstanceStatusBadge status={inst.status} />
                      <div>
                        <div className="text-sm font-medium text-slate-900">
                          {inst.agent?.name || 'No agent'}
                        </div>
                        <div className="text-xs text-slate-400">
                          {inst.prompt?.slice(0, 60) || 'No prompt'}
                          {inst.prompt && inst.prompt.length > 60 ? '...' : ''}
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-slate-400">
                      {relativeTime(inst.createdAt)}
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">No instances have run on this runner yet.</p>
            )}
          </div>
        </div>

        {/* Info sidebar */}
        <div className="space-y-6">
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="mb-4 text-sm font-semibold text-slate-900">Details</h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-slate-400">Hostname</dt>
                <dd className="mt-0.5 font-medium text-slate-700">{runner.hostname}</dd>
              </div>
              <div>
                <dt className="text-slate-400">Status</dt>
                <dd className="mt-0.5 flex items-center gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${statusDot(runner.status)}`} />
                  <span className="font-medium text-slate-700">{statusLabel(runner.status)}</span>
                </dd>
              </div>
              {runner.osInfo && (
                <>
                  <div>
                    <dt className="text-slate-400">Platform</dt>
                    <dd className="mt-0.5 flex items-center gap-1.5 font-medium text-slate-700">
                      <Cpu className="h-3.5 w-3.5 text-slate-400" />
                      {runner.osInfo.platform} / {runner.osInfo.arch}
                    </dd>
                  </div>
                  {runner.osInfo.nodeVersion && (
                    <div>
                      <dt className="text-slate-400">Node.js</dt>
                      <dd className="mt-0.5 font-medium text-slate-700">{runner.osInfo.nodeVersion}</dd>
                    </div>
                  )}
                </>
              )}
              <div>
                <dt className="text-slate-400">Capabilities</dt>
                <dd className="mt-0.5 flex flex-wrap gap-1">
                  {runner.capabilities.map((cap) => (
                    <span
                      key={cap}
                      className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
                    >
                      {cap}
                    </span>
                  ))}
                </dd>
              </div>
              <div>
                <dt className="text-slate-400">Last Seen</dt>
                <dd className="mt-0.5 flex items-center gap-1.5 font-medium text-slate-700">
                  <Clock className="h-3.5 w-3.5 text-slate-400" />
                  {relativeTime(runner.lastSeenAt)}
                </dd>
              </div>
              <div>
                <dt className="text-slate-400">Created</dt>
                <dd className="mt-0.5 font-medium text-slate-700">
                  {new Date(runner.createdAt).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
      )}

      {activeTab === 'config' && (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Claude Configuration</h2>
          <div className="flex gap-2">
            <button
              onClick={() => refetchConfig()}
              disabled={configLoading}
              className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${configLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleSaveConfig}
              disabled={updateConfig.isPending}
              className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {updateConfig.isPending ? 'Saving...' : 'Save & Push'}
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <label className="mb-2 block text-sm font-medium text-slate-700">
            settings.json
          </label>
          <textarea
            value={configSettings}
            onChange={(e) => setConfigSettings(e.target.value)}
            rows={12}
            spellCheck={false}
            className="w-full rounded-lg border border-slate-300 bg-slate-50 px-4 py-3 font-mono text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            placeholder="{}"
          />
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <label className="mb-2 block text-sm font-medium text-slate-700">
            CLAUDE.md
          </label>
          <textarea
            value={configClaudeMd}
            onChange={(e) => setConfigClaudeMd(e.target.value)}
            rows={16}
            spellCheck={false}
            className="w-full rounded-lg border border-slate-300 bg-slate-50 px-4 py-3 font-mono text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            placeholder="# CLAUDE.md content..."
          />
        </div>
      </div>
      )}
    </div>
  );
}
